import type { ProductRowPriceAndStock_ProductFragment } from '@gql-generated/graphql'
import { Availability } from '@gql-generated/graphql'
import { type ProductTierPriceProps } from '../components/product-tiers-prices/ProductTiersPrices.client'

/**
 * Filters out products that are not available for purchase.
 * - StoppedInstore as sole availability → filtered out
 * - StoppedOnline present (regardless of position) → filtered out
 */
export function filterAvailableProducts<
  T extends { stock: { availability: (Availability | null)[] } },
>(products: T[]): T[] {
  return products.filter((product) => {
    return !(
      (product.stock.availability.length === 1 &&
        product.stock.availability.includes(Availability.StoppedInstore)) ||
      product.stock.availability.includes(Availability.StoppedOnline)
    )
  })
}

/**
 * The API can return a malformed `name` (see ECOM-25447 details for cases like `{}` instead of a string).
 * Rendering it directly as a React child crashes the whole page, so any product name must go through this guard first.
 */
export function getSafeProductName(name: unknown): string {
  return typeof name === 'string' ? name : ''
}

type ArticleAvailabilityInfo = StrictPick<ProductRowPriceAndStock_ProductFragment, 'canAddToCart'>

export function isArticleReallySellable<T extends ArticleAvailabilityInfo>(article: T): boolean {
  return Boolean(article?.canAddToCart)
}

/**
 * Normalizes and validates the provided tier prices,
 * keeping only valid tiers (numeric quantity and exclTax > 0),
 * then sorts them by ascending quantity.
 */
function getSortedValidTiers(
  tiersPrices: ProductTierPriceProps[],
): { quantity: number; exclTax: number }[] {
  if (!Array.isArray(tiersPrices) || tiersPrices.length === 0) {
    return []
  }

  return tiersPrices
    .filter(
      (tier): tier is { quantity: number; exclTax: number } =>
        typeof tier.quantity === 'number' && typeof tier.exclTax === 'number' && tier.exclTax > 0,
    )
    .sort((a, b) => a.quantity - b.quantity)
}

/**
 * Returns the applicable exclTax for a given selected quantity
 * based on the provided tier prices.
 *
 * The function finds the highest tier whose quantity is less than
 * or equal to the selected quantity.
 *
 * @param tiersPrices - The list of tier prices (sorted ascending by quantity)
 * @param selectedQuantity - The currently selected quantity
 * @returns The exclTax of the applicable tier, or undefined if no match
 */
export function getTierPriceForQuantity(
  tiersPrices: ProductTierPriceProps[],
  selectedQuantity: number,
): number | undefined {
  if (selectedQuantity <= 0) return undefined

  const validTiers = getSortedValidTiers(tiersPrices)
  if (validTiers.length === 0) return undefined

  // Filter tiers whose quantity is <= the selected quantity
  const applicableTiers = validTiers.filter((tier) => tier.quantity <= selectedQuantity)
  if (applicableTiers.length === 0) return undefined

  // Return the highest applicable tier
  const lastTier = applicableTiers[applicableTiers.length - 1]

  return lastTier?.exclTax ?? undefined
}

/**
 * Returns the next tier (quantity + exclTax) for a given selected quantity.
 * This is used to display information about how many more units are needed
 * to unlock the next discounted price.
 *
 * The function finds the first tier whose quantity is strictly greater
 * than the selected quantity.
 *
 * @param tiersPrices - The list of tier prices (sorted ascending by quantity)
 * @param selectedQuantity - The currently selected quantity
 * @returns The next tier { quantity, exclTax } or undefined if already at max tier
 */
export function getNextTierPriceForQuantity(
  tiersPrices: ProductTierPriceProps[],
  selectedQuantity: number,
): { quantity: number; exclTax: number } | undefined {
  if (selectedQuantity < 0) return undefined

  const validTiers = getSortedValidTiers(tiersPrices)
  if (validTiers.length === 0) return undefined

  return validTiers.find((tier) => tier.quantity > selectedQuantity)
}
